/**
 * Firebase Cloud Messaging (FCM) — client bootstrap.
 *
 * Config is fetched at RUNTIME from `/api/push/config` (populated via the
 * super-admin `/admin/firebase` wizard). Falls back to `REACT_APP_FIREBASE_*`
 * env vars so long-term users of the env-based flow keep working.
 *
 * Public API
 * ----------
 *   getPushStatus()            → { supported, permission, enabled, tokenSaved }
 *   enablePush(apiClient)      → user-triggered: request permission + register token
 *   registerPushToken(apiClient)  legacy alias (auto-fire, no prompt if denied)
 */

import { initializeApp, getApps, deleteApp, type FirebaseApp } from "firebase/app";
import {
    getMessaging,
    getToken,
    onMessage,
    isSupported,
    type Messaging,
} from "firebase/messaging";
import { api } from "@/features/auth/api";

type FbCfg = {
    enabled: boolean;
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    vapidKey: string;
};

let _cfg: FbCfg | null = null;
let _cfgFetch: Promise<FbCfg | null> | null = null;
let _app: FirebaseApp | null = null;
let _messaging: Messaging | null = null;
let _lastToken: string | null = null;

/** Resolve config from backend (cached). Falls back to build-time env. */
async function loadConfig(): Promise<FbCfg | null> {
    if (_cfg) return _cfg;
    if (_cfgFetch) return _cfgFetch;
    _cfgFetch = (async () => {
        try {
            const { data } = await api.get<FbCfg>("/push/config");
            if (data && data.apiKey && data.projectId && data.appId && data.vapidKey) {
                _cfg = data;
                return _cfg;
            }
        } catch {
            /* fall through to env fallback */
        }
        const env: FbCfg = {
            enabled: true,
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? "",
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? "",
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? "",
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? "",
            messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? "",
            appId: process.env.REACT_APP_FIREBASE_APP_ID ?? "",
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY ?? "",
        };
        if (env.apiKey && env.projectId && env.appId && env.vapidKey) {
            _cfg = env;
            return _cfg;
        }
        return null;
    })();
    return _cfgFetch;
}

async function ensureMessaging(): Promise<{ messaging: Messaging; cfg: FbCfg } | null> {
    const cfg = await loadConfig();
    if (!cfg) return null;
    try {
        // Rebuild if the config surface has changed (admin rotated keys).
        const existing = getApps()[0];
        if (existing && (existing.options as { apiKey?: string }).apiKey !== cfg.apiKey) {
            try {
                await deleteApp(existing);
            } catch {
                /* ignore */
            }
            _app = null;
            _messaging = null;
        }
        if (!_app) _app = getApps()[0] ?? initializeApp(cfg);
        if (!_messaging) _messaging = getMessaging(_app);
        return { messaging: _messaging, cfg };
    } catch {
        return null;
    }
}

export type PushStatus = {
    supported: boolean;
    supportReason?: string;
    permission: "default" | "granted" | "denied" | "unavailable";
    firebaseReady: boolean;
    tokenSaved: boolean;
};

/** Instant, non-blocking status check for UI. */
export async function getPushStatus(): Promise<PushStatus> {
    // iOS <16.4 / desktop Safari without prior "add to home screen" don't
    // expose Notification at all. Firebase's isSupported() also rejects
    // some Safari versions even when Notification exists.
    if (typeof window === "undefined" || !("Notification" in window)) {
        return {
            supported: false,
            supportReason: "Your browser doesn't support web notifications yet.",
            permission: "unavailable",
            firebaseReady: false,
            tokenSaved: false,
        };
    }
    const perm = Notification.permission as PushStatus["permission"];
    let supported = true;
    let supportReason: string | undefined;
    try {
        supported = await isSupported();
        if (!supported) supportReason = "Push isn't supported in this browser.";
    } catch {
        supported = false;
        supportReason = "Push isn't supported in this browser.";
    }
    const cfg = await loadConfig();
    return {
        supported,
        supportReason,
        permission: perm,
        firebaseReady: Boolean(cfg && cfg.enabled),
        tokenSaved: Boolean(_lastToken),
    };
}

async function registerSw(): Promise<ServiceWorkerRegistration | null> {
    if (!("serviceWorker" in navigator)) return null;
    try {
        // A dedicated scope so it never fights the shell SW.
        return await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch {
        return null;
    }
}

/**
 * User-triggered permission request + FCM token registration.
 * Returns the outcome so the UI can render clear state.
 */
export async function enablePush(apiClient: {
    post: (path: string, body: unknown) => Promise<unknown>;
} = api as unknown as {
    post: (path: string, body: unknown) => Promise<unknown>;
}): Promise<{ ok: boolean; token: string | null; reason?: string }> {
    if (typeof window === "undefined" || !("Notification" in window)) {
        return { ok: false, token: null, reason: "notifications-unsupported" };
    }
    try {
        const supported = await isSupported();
        if (!supported) return { ok: false, token: null, reason: "unsupported-browser" };
    } catch {
        return { ok: false, token: null, reason: "unsupported-browser" };
    }

    let perm = Notification.permission;
    if (perm === "default") {
        perm = await Notification.requestPermission();
    }
    if (perm === "denied") return { ok: false, token: null, reason: "permission-denied" };
    if (perm !== "granted") return { ok: false, token: null, reason: "permission-dismissed" };

    const ctx = await ensureMessaging();
    if (!ctx) return { ok: false, token: null, reason: "not-configured" };

    const swReg = await registerSw();
    try {
        const token = await getToken(ctx.messaging, {
            vapidKey: ctx.cfg.vapidKey,
            serviceWorkerRegistration: swReg ?? undefined,
        });
        if (!token) return { ok: false, token: null, reason: "no-token" };
        await apiClient.post("/push/register", { token, platform: "web" });
        _lastToken = token;
        onMessage(ctx.messaging, (payload) => {
            const n = payload.notification;
            if (n?.title && "Notification" in window) {
                new Notification(n.title, { body: n.body ?? "" });
            }
        });
        return { ok: true, token };
    } catch (err) {
        console.warn("[push] enable failed", err);
        return { ok: false, token: null, reason: "token-failed" };
    }
}

/**
 * Legacy auto-fire — used by AuthContext right after login. Does NOT prompt
 * the user; only reuses an existing "granted" permission. This is the safe
 * default; the user-triggered `enablePush()` handles the first-time prompt.
 */
export async function registerPushToken(apiClient: {
    post: (path: string, body: unknown) => Promise<unknown>;
} = api as unknown as {
    post: (path: string, body: unknown) => Promise<unknown>;
}): Promise<string | null> {
    if (typeof window === "undefined" || !("Notification" in window)) return null;
    if (Notification.permission !== "granted") return null;
    const res = await enablePush(apiClient);
    return res.token ?? null;
}

export async function isFirebaseConfigured(): Promise<boolean> {
    const cfg = await loadConfig();
    return Boolean(cfg && cfg.enabled);
}
